// Globals
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuProps } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { productsSelector, getProduct } from '../../feature';

// Components
import { StyledPageWrapper } from '../../components/Styled';
import {
  StyledHeaderWrapper,
  StyledMenu,
  StyledPageTitle,
  StyledTextButton,
} from './ProductDetailsMainContainerStyles';
import { ChevronIcon } from '@/icons';

// Helpers
import { ProductDetailsTabs, getProductDetailsTabsConfig } from '../../helpers/configs';
import { useBoolean } from '@/hooks';

const ProductDetailsMainContainer = () => {
  const { t } = useTranslation(['products', 'common']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { value: hasUnsavedChanges, setTrue: setHasUnsavedChanges, setFalse: clearUnsavedChanges } = useBoolean();
  const productDetailsTabsConfig = useMemo(() => getProductDetailsTabsConfig(t), []);
  const [selectedKey, setSelectedKey] = useState(productDetailsTabsConfig[0].key);
  const [pendingTab, setPendingTab] = useState<ProductDetailsTabs | null>(null);

  const selectedProductDetailsTab = useMemo(
    () => productDetailsTabsConfig.find((item) => item.key === selectedKey),
    [selectedKey],
  );
  const { product } = useAppState(productsSelector);

  const goBack = () => {
    navigate(-1);
  };

  const onMenuButtonClick: MenuProps['onClick'] = (e) => {
    const newTab = e.key as ProductDetailsTabs;

    if (hasUnsavedChanges && newTab !== selectedKey) {
      setPendingTab(newTab);
      return;
    }
    setSelectedKey(newTab);
  };

  const handleConfirmTabSwitch = () => {
    if (pendingTab) {
      setSelectedKey(pendingTab);
      setPendingTab(null);
    }
  };

  const handleDeclineTabSwitch = () => {
    if (pendingTab) {
      setPendingTab(null);
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(getProduct(id));
    }
  }, [id]);

  return (
    <StyledPageWrapper>
      <StyledHeaderWrapper>
        <StyledTextButton onClick={goBack}>
          <ChevronIcon />
          {t('button_common_back', { ns: 'common' })}
        </StyledTextButton>
        <StyledPageTitle>{product?.name}</StyledPageTitle>
        <StyledMenu
          mode="horizontal"
          items={productDetailsTabsConfig}
          defaultSelectedKeys={[selectedKey]}
          selectedKeys={[selectedKey]}
          onClick={onMenuButtonClick}
        />
      </StyledHeaderWrapper>

      {selectedProductDetailsTab!.content({
        hasUnsavedChanges,
        setHasUnsavedChanges,
        clearUnsavedChanges,
        pendingTab,
        handleConfirmTabSwitch,
        handleDeclineTabSwitch,
        onNavigateToTab: setSelectedKey,
      })}
    </StyledPageWrapper>
  );
};

export default ProductDetailsMainContainer;
